import _styled from "styled-components";
import parse, { type DOMNode, type HTMLReactParserOptions, domToReact, Element, type Text } from "html-react-parser";
import { Link } from "~/features/ui/link";
import { CodeBlock } from "~/features/ui/code-block";
type Props = {
  text: string;
};
const parserOptions: HTMLReactParserOptions = {
  replace: domNode => {
    if (domNode instanceof Element && domNode.attribs) {
      // replace "a" tags with the Link component
      if (domNode.name === "a") {
        const href = domNode.attribs.href;
        const target = domNode.attribs.target;
        const rel = domNode.attribs.rel;
        const props = {
          href,
          target,
          rel
        };
        return <Link {...props}>{domToReact(domNode.children as DOMNode[], parserOptions)}</Link>;
      }
      if (domNode.name === "pre") {
        const codeElement = domNode.children[0];
        let codeText = "";

        // Extract the code text from the "code" tag.
        if (codeElement instanceof Element && codeElement.name === "code") {
          codeText = (codeElement.children[0] as Text).data;
        }
        return <CodeBlock text={codeText} />;
      }
    }
  }
};
const StyledRichText = /*#__PURE__*/_styled.div.withConfig({
  displayName: "rich-text__StyledRichText",
  componentId: "sc-dhigx6-0"
})(["", " h2{", "}h3{", "}h6{", "}ul,ol{", "}ol{", " li{", "}}ul{", " li{", "}ul{", " li{", "}}}strong,b{", "}code,kbd,samp,pre{", "}a{", "}img{", "}iframe{", "}@media (max-width:1024px){br{", "}}"], {
  "maxWidth": "100%",
  "> :not([hidden]) ~ :not([hidden])": {
    "--tw-space-y-reverse": "0",
    "marginTop": "calc(2.4rem * calc(1 - var(--tw-space-y-reverse)))",
    "marginBottom": "calc(2.4rem * var(--tw-space-y-reverse))"
  }
}, {
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "&:not(:last-child)": {
    "marginTop": "4rem",
    "marginBottom": "4rem"
  },
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
}, {
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "&:not(:last-child)": {
    "marginTop": "3.6rem",
    "marginBottom": "3.6rem"
  },
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
}, {
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "&:not(:last-child)": {
    "marginTop": "2.4rem",
    "marginBottom": "2.4rem"
  },
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
}, {
  "paddingLeft": "2.4rem"
}, {
  "listStyleType": "decimal",
  "& *::marker": {
    "marginRight": "0.8rem",
    "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400",
    "color": "rgb(92 92 92 )"
  },
  "&::marker": {
    "marginRight": "0.8rem",
    "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400",
    "color": "rgb(92 92 92 )"
  }
}, {
  "&:not(:first-child)": {
    "marginTop": "1.6rem"
  }
}, {
  "listStyleType": "none"
}, {
  "position": "relative",
  "&::before": {
    "content": "var(--tw-content)",
    "position": "absolute",
    "left": "-2rem",
    "top": "0.8rem",
    "display": "block",
    "height": "0.8rem",
    "width": "0.8rem",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
  },
  "&:not(:first-child)": {
    "marginTop": "1.6rem"
  }
}, {
  "marginTop": "0.8rem"
}, {
  "&::before": {
    "content": "var(--tw-content)",
    "left": "-1.6rem",
    "top": "1rem",
    "height": "0.4rem",
    "width": "0.4rem"
  }
}, {
  "fontWeight": "700"
}, {
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
}, {
  "--tw-text-opacity": "1",
  "color": "rgb(75 46 248 / var(--tw-text-opacity))",
  "textDecorationLine": "underline",
  "textUnderlineOffset": "4px"
}, {
  "width": "100%"
}, {
  "aspectRatio": "16 / 9",
  "height": "100%",
  "width": "100%"
}, {
  "display": "none"
});
export function RichText(props: Props & React.ComponentPropsWithRef<"div">) {
  const {
    text,
    ...rest
  } = props;
  return <StyledRichText {...rest}>{parse(text, parserOptions)}</StyledRichText>;
}